import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { VALIDATION_SCHEMAS } from '../../../constants';
import { BasicModal, Button, SelectController } from '../../Shared';

const getCountries = () => fetch('/countries');
const getStates = countryCode => fetch(`/states?countryCode=${countryCode}`);
const getCities = (countryCode, stateCode) =>
  fetch(
    `/cities?countryCode=${countryCode}` +
      (stateCode ? `&stateCode=${stateCode}` : ''),
  );

export const TimeZoneModal = ({ open, credentials, onSignIn, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ianaTimeZone, setIanaTimeZone] = useState('');
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isLocationError, setIsLocationError] = useState(false);

  const hasCountries = countries.length > 0;
  const hasStates = states.length > 0;
  const hasCities = cities.length > 0;
  const isStateRequired = hasStates && !isLocationLoading;

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: yupResolver(VALIDATION_SCHEMAS.LOGIN_TIME_ZONE(isStateRequired)),
    defaultValues: { country: '', state: '', city: '' },
  });

  const updateStates = async countryName => {
    setIsLocationLoading(true);
    setIanaTimeZone('');
    setValue('state', '');
    setValue('city', '');
    setStates([]);
    setCities([]);

    const country = countries.find(v => v.name === countryName);

    if (country.statesCount > 0) {
      const statesResponse = await getStates(country.isoCode);
      const states = await statesResponse.json();
      setStates(states);
    } else {
      const citiesResponse = await getCities(country.isoCode);
      const cities = await citiesResponse.json();
      setCities(cities);
    }
    setIsLocationLoading(false);
  };

  const updateCities = async stateName => {
    setIsLocationLoading(true);
    setIanaTimeZone('');
    setValue('city', '');
    setCities([]);
    const countryName = getValues('country');
    const country = countries.find(v => v.name === countryName);
    const state = states.find(v => v.name === stateName);
    const citiesResponse = await getCities(country.isoCode, state.isoCode);
    const cities = await citiesResponse.json();
    setCities(cities);
    setIsLocationLoading(false);
  };

  const updateIanaTimeZone = cityName => {
    const city = cities.find(v => v.name === cityName);
    setIanaTimeZone(city.timezone);
  };

  const onSubmit = handleSubmit(async data => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/update-iana-timezone', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...credentials, ...data }),
      });

      if (response.status !== 200) throw new Error();

      setIsSubmitting(false);
      onClose();
      onSignIn();
    } catch {
      setIsSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  });

  const fetchCounties = useCallback(async () => {
    setIsLocationLoading(true);
    setIsLocationError(false);

    try {
      const countriesResponse = await getCountries();
      const countries = await countriesResponse.json();
      setCountries(countries);
      setIsLocationLoading(false);
    } catch {
      setIsLocationLoading(false);
      setIsLocationError(true);
      toast.error("Can't get Countries", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    fetchCounties();
  }, [fetchCounties]);

  const isSelectDisabled = isLocationLoading || isLocationError;
  const isCountriesSelectDisabled = !hasCountries || isSelectDisabled;
  const isStatesSelectDisabled = !hasStates || isSelectDisabled;
  const isCitiesSelectDisabled = !hasCities || isSelectDisabled;
  const hasIanaTimeZone = !!ianaTimeZone;

  return (
    <BasicModal
      open={open}
      handleClose={isSubmitting ? undefined : onClose}
      className="flex items-center justify-center w-full h-full"
    >
      <div className="bg-white w-[576px] rounded-3xl shadow-[-40px_40px_80px_-8px_rgba(0,0,0,0.24)]">
        <div className="flex items-center justify-between py-4 pl-5 pr-4 border-b border-b-base-200">
          <span className="text-base-1000 font-semibold text-[22px]/[28px]">
            Location and Time Zone
          </span>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-sm font-semibold text-gray-800 transition hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 dark:text-white dark:hover:bg-gray-700 dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-gray-600"
            data-hs-overlay="#edit-contact-modal"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-4 w-4 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col gap-5 py-4 px-5">
          <div className="flex gap-4">
            <SelectController
              control={control}
              name="country"
              label="Country"
              placeholder="Select Country"
              options={countries.map((v, idx) => ({
                id: idx,
                label: v.name,
                value: v.name,
              }))}
              disabled={isCountriesSelectDisabled}
              handleChange={e => {
                updateStates(e.target.value);
              }}
            />
            <SelectController
              control={control}
              name="state"
              label="State"
              placeholder="Select State"
              options={states.map((v, idx) => ({
                id: idx,
                label: v.name,
                value: v.name,
              }))}
              disabled={isStatesSelectDisabled}
              handleChange={e => {
                updateCities(e.target.value);
              }}
            />
            <SelectController
              control={control}
              name="city"
              label="City"
              placeholder="Select City"
              options={cities.map((v, idx) => ({
                id: idx,
                label: v.name,
                value: v.name,
              }))}
              disabled={isCitiesSelectDisabled}
              handleChange={e => {
                updateIanaTimeZone(e.target.value);
              }}
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="font-medium text-[16px]/[24px] text-base-800">
              Timezone:
            </span>
            <span
              className={`text-[20px]/[24px] ${hasIanaTimeZone ? 'text-base-1000' : 'text-base-600'}`}
            >
              {hasIanaTimeZone ? ianaTimeZone : 'Not selected'}
            </span>
          </div>
        </div>
        <div className="flex justify-end py-4 px-5 border-t border-t-base-200">
          <Button title="Save" onClick={onSubmit} disabled={isSubmitting} />
        </div>
      </div>
    </BasicModal>
  );
};
